import React, { useState } from "react";

const OPCIONES_ESTADO = [
  { value: "presente", label: "Presente", icon: "bi-check-circle-fill", clase: "text-success" },
  { value: "virtual", label: "Presente (clase virtual)", icon: "bi-camera-video-fill", clase: "text-info" },
  { value: "ausente", label: "Ausente", icon: "bi-x-circle-fill", clase: "text-danger" },
  { value: "justificado", label: "Justificado", icon: "bi-shield-fill-check", clase: "text-warning" },
];

function ModalConfirmarQuitar({ estudiante, onConfirmar, onCancelar }) {
  if (!estudiante) return null;
  return (
    <div className="modal d-block" style={{ background: "rgba(0,0,0,.45)", zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
        <div className="modal-content">
          <div className="modal-header border-0 pb-0">
            <h6 className="modal-title fw-bold">
              <i className="bi bi-eraser-fill text-danger me-2" />
              Quitar marca de asistencia
            </h6>
            <button type="button" className="btn-close" onClick={onCancelar} />
          </div>
          <div className="modal-body pt-2">
            <p className="mb-0 text-muted small">
              ¿Quitar la marca de asistencia de <strong>{estudiante.nombre_completo}</strong> en esta materia?
              Vuelve a quedar "sin registro".
            </p>
          </div>
          <div className="modal-footer border-0 pt-1 gap-2">
            <button className="btn btn-secondary btn-sm" onClick={onCancelar}>Cancelar</button>
            <button className="btn btn-sm btn-danger" onClick={onConfirmar}>Quitar marca</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Detalle fino por materia/bloque — independiente del registro "día completo".
// No recalcula el % oficial de asistencia ni la mención de honor, es solo
// bitácora para cuando coordinación necesita precisar a qué materia exacta
// faltó un estudiante. Estructura de estudiantes es plana (no hay celdas por
// sesión como en AsistenciaSesionModal, porque aquí ya estamos en un bloque).
export default function AsistenciaBloqueModal({
  bloque,
  estudiantes,
  puedeEditar,
  onGuardar,
  onEliminar,
  onCerrar,
  onVolver,
}) {
  const [guardandoId, setGuardandoId] = useState(null);
  const [observaciones, setObservaciones] = useState({});
  const [confirmarQuitar, setConfirmarQuitar] = useState(null);

  const handleCambiar = async (estudianteId, estado) => {
    if (!puedeEditar) return;
    setGuardandoId(estudianteId);
    try {
      await onGuardar(estudianteId, estado, observaciones[estudianteId] || "");
    } finally {
      setGuardandoId(null);
    }
  };

  const handleConfirmarEliminar = async () => {
    const estudianteId = confirmarQuitar?.estudiante_id;
    setConfirmarQuitar(null);
    if (!estudianteId) return;
    setGuardandoId(estudianteId);
    try {
      await onEliminar?.(estudianteId);
    } finally {
      setGuardandoId(null);
    }
  };

  return (
    <div
      className="modal fade show"
      style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
      tabIndex="-1"
      role="dialog"
    >
      <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {onVolver && (
                <button type="button" className="btn btn-sm btn-outline-secondary me-2" onClick={onVolver} title="Volver a las materias del día">
                  <i className="bi bi-arrow-left" />
                </button>
              )}
              <i className="bi bi-journal-bookmark me-2" />
              {bloque.materia_nombre || "Materia"} — {bloque.hora_inicio?.slice(0, 5)}–{bloque.hora_fin?.slice(0, 5)}
            </h5>
            <button type="button" className="btn-close" onClick={onCerrar} />
          </div>

          <div className="modal-body">
            <div className="alert alert-info py-2 px-3 mb-3" style={{ fontSize: "0.85rem" }}>
              <i className="bi bi-info-circle me-1" />
              Este detalle por materia es informativo — no reemplaza ni recalcula la asistencia
              del día completo ni la elegibilidad de mención de honor.
            </div>
            {!puedeEditar && (
              <div className="alert alert-secondary py-2 px-3 mb-3" style={{ fontSize: "0.85rem" }}>
                <i className="bi bi-eye me-1" /> Solo lectura — no tienes permiso para justificar asistencia.
              </div>
            )}

            <div className="d-flex flex-column gap-2">
              {estudiantes.map((est) => {
                const observacionActual = observaciones[est.estudiante_id] ?? est.observacion ?? "";
                const tieneMarcaManual = est.origen === "manual";
                return (
                  <div
                    key={est.estudiante_id}
                    className="d-flex flex-wrap align-items-center gap-2 p-2 rounded border"
                  >
                    <div className="me-auto">
                      <div className="fw-semibold">{est.nombre_completo}</div>
                      <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                        {est.modalidad === "online" ? (
                          <span><i className="bi bi-camera-video me-1" />Online</span>
                        ) : (
                          <span><i className="bi bi-person me-1" />Presencial</span>
                        )}
                        {est.origen === "sin_registro" && " · sin registro"}
                      </div>
                    </div>

                    {puedeEditar && (
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        style={{ maxWidth: 220 }}
                        placeholder="Observación (opcional)"
                        value={observacionActual}
                        disabled={guardandoId === est.estudiante_id}
                        onChange={(e) =>
                          setObservaciones((prev) => ({ ...prev, [est.estudiante_id]: e.target.value }))
                        }
                      />
                    )}
                    {!puedeEditar && est.observacion && (
                      <span className="text-muted" style={{ fontSize: "0.78rem" }}>{est.observacion}</span>
                    )}

                    <div className="btn-group btn-group-sm" role="group">
                      {OPCIONES_ESTADO.map((op) => {
                        const activo = est.estado === op.value;
                        return (
                          <button
                            key={op.value}
                            type="button"
                            className={`btn ${activo ? "btn-secondary" : "btn-outline-secondary"}`}
                            disabled={!puedeEditar || guardandoId === est.estudiante_id}
                            onClick={() => handleCambiar(est.estudiante_id, op.value)}
                            title={op.label}
                          >
                            {guardandoId === est.estudiante_id ? (
                              <span className="spinner-border spinner-border-sm" />
                            ) : (
                              <i className={`bi ${op.icon} ${activo ? "" : op.clase}`} />
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {puedeEditar && tieneMarcaManual && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        disabled={guardandoId === est.estudiante_id}
                        onClick={() => setConfirmarQuitar(est)}
                        title="Quitar marca (volver a sin registro)"
                      >
                        <i className="bi bi-eraser-fill" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={onCerrar}>
              Cerrar
            </button>
          </div>
        </div>
      </div>

      <ModalConfirmarQuitar
        estudiante={confirmarQuitar}
        onConfirmar={handleConfirmarEliminar}
        onCancelar={() => setConfirmarQuitar(null)}
      />
    </div>
  );
}
